<script lang="ts">
    import EekLabel from '@tile/components/eek/EekLabel.svelte';
    import OttoUp from '@tile/components/otto-up/OttoUp.svelte';
    import DealBadge from '@tile/components/product-image/DealBadge.svelte';
    import type { ImageAspectRatio } from '@tile/components/product-image/types.ts';
    import Rating from '@tile/components/rating/Rating.svelte';
    import SponsoredHint from '@tile/components/sponsored-hint/SponsoredHint.svelte';
    import TopInfo from '@tile/components/top-info/TopInfo.svelte';
    import PriceDispatcher from '@tilelist/layouts/dispatchers/price-dispatcher/PriceDispatcher.svelte';
    import Availability from '@tilelist/layouts/experiments/e3867/availability/Availability.svelte';
    import Card from '@tilelist/layouts/experiments/e3867/card/Card.svelte';
    import ColorThumbs from '@tilelist/layouts/experiments/e3867/color-thumbs/ColorThumbs.svelte';
    import type { DefaultLayout } from '@tilelist/layouts/experiments/e3867/default-layout/DefaultLayout.ts';
    import ProductBrand from '@tilelist/layouts/experiments/e3867/product-brand/ProductBrand.svelte';
    import ProductHeader from '@tilelist/layouts/experiments/e3867/product-header/ProductHeader.svelte';
    import ProductImageLink from '@tilelist/layouts/experiments/e3867/product-image-link/ProductImageLink.svelte';
    import ProductImage from '@tilelist/layouts/experiments/e3867/product-image/ProductImage.svelte';
    import ProductName from '@tilelist/layouts/experiments/e3867/product-name/ProductName.svelte';
    import SocialProofBadges from '@tilelist/layouts/experiments/e3867/social-proof-badges/SocialProofBadges.svelte';
    import type { TileLayoutProps } from '@tilelist/layouts/shared/TileLayoutProps.ts';

    type Props = TileLayoutProps & {
        layout: DefaultLayout;
        aspectRatio: ImageAspectRatio;
    };

    let {
        layout,
        aspectRatio,
        imageLoadingOptions,
        onRedirectToPdp,
        rule,
        colors,
        sponsored,
        funderLegalName,
        advertiserLegalName,
        element = $bindable(null),
    }: Props = $props();

    const { variation, title, link } = $derived(layout);
</script>

<Card
    data-jarvis="reptile-tile-container"
    data-variation-id={variation.variationId}
    bind:element
    disableOverlayEffect
    size="50"
>
    <!-- NOTE: This is used in the Card component and is passed through -->
    {#snippet primaryAction()}
        <a
            id={`link-${variation.variationId}`}
            href={link.href}
            data-primary-action
            aria-label={variation.title?.withoutBrand || variation.title?.full || ''}
            onclick={onRedirectToPdp}
        ></a>
    {/snippet}

    <div class="reptile-clean-layout__image">
        <ProductImage {aspectRatio}>
            <ProductImageLink
                image={layout.primaryImage}
                variationId={variation.variationId}
                detailPageLink={variation.detailPageLink}
                onClick={onRedirectToPdp}
                {imageLoadingOptions}
                {aspectRatio}
                shouldDim={layout.shouldDim}
            />
        </ProductImage>
        <!-- DealBadge and SocialProofBadges are self-positioning atoms (own position: absolute),
             rendered as flat siblings here rather than nested inside ProductImage/ProductImageLink,
             so both stay independently clickable/composable (see README). -->
        {#if variation.deal}
            <DealBadge deal={variation.deal} variationId={variation.variationId} {rule} />
        {/if}
        <!-- TODO: Should we break these up? -->
        <SocialProofBadges socialProof={variation.socialProof} />
    </div>

    <ProductHeader>
        {#snippet brand()}
            <ProductBrand brand={title.brand} />
        {/snippet}
        {#snippet name()}
            <ProductName name={title.display} />
        {/snippet}
        {#snippet topInfos()}
            <TopInfo variant="clean" infos={variation.topInfos} />
        {/snippet}
        {#snippet rating()}
            <div data-secondary-action>
                <Rating variant="clean" showNumberOfReviews customerReviews={variation.customerReviews} />
            </div>
        {/snippet}
    </ProductHeader>

    <div data-secondary-action>
        <EekLabel labels={variation.energyLabels} asRow />
    </div>

    <PriceDispatcher
        variationId={variation.variationId}
        saleTags={variation.saleTags}
        price={variation.price}
        dealHighlight={variation.deal?.highlight}
        {rule}
    />

    {#if variation.availability || variation.ottoUp}
        <div class="reptile-clean-layout__row">
            <Availability availability={variation.availability} />
            <OttoUp show={variation.ottoUp} />
        </div>
    {/if}

    {#if variation.colors || sponsored}
        <div class="reptile-clean-layout__row">
            <ColorThumbs {colors} />
            <div data-secondary-action>
                <SponsoredHint
                    sponsored={sponsored ?? false}
                    funderLegalName={funderLegalName ?? ''}
                    advertiserLegalName={advertiserLegalName ?? ''}
                />
            </div>
        </div>
    {/if}
</Card>

<style lang="scss">
    @use '@shared/styles/mixins' as reptileMixins;

    .reptile-clean-layout {
        &__image {
            position: relative;
        }

        &__row {
            @include reptileMixins.row-space-between;
        }
    }
</style>
